import { useEffect, useRef, type ClipboardEvent, type Ref } from 'react';
import { shake } from '@lib/utils/animate';

interface Props {
  id?: string;
  value: string;
  /** digits only, at most `length` */
  onChange: (digits: string) => void;
  /** the last digit arrived (typed, pasted or filled in by the phone's one-time-code suggestion) */
  onComplete?: (code: string) => void;
  length: number;
  inputRef?: Ref<HTMLInputElement>;
  invalid?: boolean;
  disabled?: boolean;
  /** while a code is being checked: the field keeps the focus and the phone keeps its keyboard (unlike `disabled`) */
  readOnly?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  'aria-describedby'?: string;
}

const digits = (s: string, length: number) => s.replace(/\D/g, '').slice(0, length);

/**
 * A one-time code (authenticator, SMS): one field, so typing, pasting "123 456" or "123-456", the phone's code
 * suggestion and a password manager all work, and a screen reader hears one input. Numeric keypad on phones; anything
 * but digits is dropped. `onComplete` fires when the code becomes whole (or a whole code is replaced by another).
 * A code refused (`invalid` turning on) shakes the field once, unless a card around it shakes itself (`data-no-shake`).
 */
export function CodeInput({ id, value, onChange, onComplete, length, inputRef, invalid, disabled, readOnly, required, autoFocus, 'aria-describedby': describedBy }: Props) {
  const own = useRef<HTMLInputElement | null>(null);
  const setRef = (el: HTMLInputElement | null) => {
    own.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) inputRef.current = el;
  };
  const was = useRef(!!invalid);
  useEffect(() => {
    if (invalid && !was.current && !own.current?.closest('[data-no-shake]')) shake(own.current);
    was.current = !!invalid;
  }, [invalid]);
  const set = (raw: string) => {
    if (readOnly) return;
    const v = digits(raw, length);
    onChange(v);
    if (v.length === length && v !== value) onComplete?.(v);
  };
  /** a pasted whole code replaces what was there (never "12" + "123456" cut to "121234") */
  const paste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = digits(e.clipboardData.getData('text'), length + 1);
    if (pasted.length < length) return;
    e.preventDefault();
    set(pasted);
  };
  return (
    <input
      ref={setRef}
      id={id}
      className="code-input mono"
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={length}
      spellCheck={false}
      autoCorrect="off"
      autoCapitalize="none"
      enterKeyHint="done"
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      autoFocus={autoFocus}
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => set(e.target.value)}
      onPaste={paste}
    />
  );
}
